import { useEffect, useMemo, useState } from "react";
import { GridLoader } from "react-spinners";

import { Composer, Container, MessageList, useWebchat } from "@botpress/webchat";
import AddIcon from "@mui/icons-material/Add";
import { Box } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";

import Dialog from "../ui/omniDialog";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";
import DialogTitle from "../ui/omniDialogTitle";

const extractMessageText = message => {
  if (message == null) {
    return "";
  }
  // Webchat SDK messages carry their text at block.block.text; keep the payload
  // fallbacks for any other message shapes.
  if (typeof message.block?.block?.text === "string") {
    return message.block.block.text;
  }
  if (typeof message.block?.text === "string") {
    return message.block.text;
  }
  if (typeof message.payload?.text === "string") {
    return message.payload.text;
  }
  if (typeof message.payload?.block?.text === "string") {
    return message.payload.block.text;
  }
  return "";
};

// How long to wait after the bot's last message before treating the analysis as
// finished (it answers in several short progress messages, then the summary).
const RESULT_SETTLE_MS = 4000;

// The summary is long; the progress messages ("Analyzing turns…") are short, so
// require a substantial captured result before enabling Save — this keeps the
// button from going live during a gap between progress messages.
const RESULT_MIN_LENGTH = 150;

const BotpressInteractiveAssessmentDialog = props => {
  const {
    open,
    onClose,
    onAccept,
    singleConversation,
    newestConversation,
    oldestConversation,
    analyticsBotODM,
    dialogTitle = "A.I. Analysis",
    okMessage = "Ok",
    cancelMessage = "Cancel",
    maxWidth = "md",
    fullWidth = true,
    // Opt-in: when provided, the dialog captures the bot's final reply and shows
    // a "Save Results" button that hands it back. When omitted (the default used
    // by the view-only toolbar comparison and single analysis), behaviour is
    // unchanged — no capture, no save button.
    onSaveComparison = null,
  } = props;

  const theme = useTheme();
  const [botData] = useState(analyticsBotODM?.omniData.botData);

  const onAcceptHandler = () => {
    onAccept();
  };

  const onCloseHandler = () => {
    onClose();
  };

  //#region Botpress Assessment Webchat Setup

  const webchatClientID = analyticsBotODM?.omniData.webchatClientId;
  const [isWebchatOpen, setIsWebchatOpen] = useState(false);

  const { client, on, messages, isTyping, user, clientState, newConversation } = useWebchat({
    clientId: webchatClientID, // Insert your Client ID here
    feedbackEnabled: false,
    storageKey: `botpress-botpressInteractiveAssessmentDialog-${webchatClientID}`,
    storageLocation: "localStorage",
  });

  const enrichedMessages = useMemo(
    () =>
      messages.map(message => {
        const { authorId } = message;
        const direction = authorId === user?.userId ? "outgoing" : "incoming";
        return {
          ...message,
          direction,
          sender:
            direction === "outgoing"
              ? { name: user?.name ?? "You", avatar: user?.pictureUrl }
              : { name: botData?.botName ?? "Bot", avatar: botData?.botAvatar },
        };
      }),
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [botData?.botName, messages, user?.userId, user?.name, user?.pictureUrl],
  );

  const [userInitialized, SetUserInitialized] = useState(false);

  useEffect(() => {
    const unsubscribe = on("conversation", event => {
      client?.sendMessage({
        type: "text",
        text: "Please analyze the conversation and provide insights.",
      });
    });

    return () => {
      unsubscribe();
    };
  }, [on, client]);

  const [countdown, setCountdown] = useState(-1);

  useEffect(() => {
    //ok start the automated process
    if (countdown > 0) {
      const countdownFunction = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 500);
      return () => clearTimeout(countdownFunction);
    } else if (countdown === 0) {
      newConversation().then(() => {
        setIsWebchatOpen(true);
      });
    }
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  useEffect(() => {
    if (client && singleConversation && !userInitialized) {
      try {
        client.updateUser({
          data: { singleConversation: JSON.stringify(singleConversation) },
        });
        SetUserInitialized(true);
        setCountdown(1);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, singleConversation, userInitialized]);

  useEffect(() => {
    if (client && newestConversation && oldestConversation && !userInitialized) {
      try {
        client.updateUser({
          data: {
            newestConversation: JSON.stringify(newestConversation),
            oldestConversation: JSON.stringify(oldestConversation),
          },
        });
        SetUserInitialized(true);
        setCountdown(1);
      } catch (error) {
        console.error(error);
      }
    }
  }, [client, newestConversation, oldestConversation, userInitialized]);

  //#endregion

  //#region Capture the final result (only when onSaveComparison is provided)
  const [resultText, setResultText] = useState("");
  const [resultReady, setResultReady] = useState(false);

  useEffect(() => {
    if (!onSaveComparison || !isWebchatOpen) {
      return undefined;
    }
    // The bot answers in several short progress messages ("Analyzing turns…")
    // and one long summary. Take the longest incoming message as the result so
    // ordering and the progress chatter don't matter, then treat the analysis as
    // finished once nothing newer has arrived for the settle window.
    const latest = enrichedMessages
      .filter(message => message.direction === "incoming")
      .map(extractMessageText)
      .reduce((longest, text) => (text.length > longest.length ? text : longest), "");
    // Always set (even to "") so a cleared message list doesn't leave stale text.
    setResultText(latest);
    setResultReady(false);
    const settle = setTimeout(() => setResultReady(true), RESULT_SETTLE_MS);
    return () => clearTimeout(settle);
  }, [enrichedMessages, onSaveComparison, isWebchatOpen]);
  //#endregion

  return (
    <Dialog
      open={open}
      onClose={() => onCloseHandler}
      disableEscapeKeyDown
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogTitle onClose={onCloseHandler}>{dialogTitle}</DialogTitle>
      <DialogContent sx={{ padding: 0, overflowX: "hidden" }}>
        {!isWebchatOpen && (
          <Box
            sx={{
              minHeight: "75vh",
              maxHeight: "75vh",
              width: "100%",
              alignContent: "center",
              textAlign: "center",
            }}
          >
            <GridLoader size={56} color={theme.palette.primary.main} />
          </Box>
        )}
        {isWebchatOpen && (
          <Container
            connected={clientState !== "disconnected"}
            style={{
              minHeight: "75vh",
              maxHeight: "75vh",
              width: "100%",
              display: isWebchatOpen ? "flex" : "none",
              borderRadius: 0,
              border: "none",
            }}
          >
            <MessageList
              botAvatar={<AddIcon />}
              // botAvatar={botData?.botAvatar}
              botName={"Assessment Bot"}
              botDescription={"Helping you analyze the conversation"}
              isTyping={isTyping}
              headerMessage="Beginning Analysis"
              showMarquee={true}
              messages={enrichedMessages}
              sendMessage={client?.sendMessage}
            />
            <Composer
              disableComposer={false}
              isReadOnly={false}
              allowFileUpload={true}
              connected={clientState !== "disconnected"}
              sendMessage={client?.sendMessage}
              uploadFile={client?.uploadFile}
              composerPlaceholder="Type a message..."
            />
          </Container>
        )}
      </DialogContent>
      <DialogActions>
        {cancelMessage && (
          <Button onClick={onCloseHandler} color="secondary" variant="contained">
            {cancelMessage}
          </Button>
        )}
        {onSaveComparison ? (
          <Button
            onClick={() =>
              onSaveComparison({ summary: resultText, comparedAt: new Date().toISOString() })
            }
            color="secondary"
            variant="contained"
            disabled={!resultReady || resultText.trim().length < RESULT_MIN_LENGTH}
          >
            {resultReady && resultText.trim().length >= RESULT_MIN_LENGTH
              ? "Save Results"
              : "Analyzing…"}
          </Button>
        ) : (
          <Button onClick={onAcceptHandler} color="secondary" variant="contained">
            {okMessage}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BotpressInteractiveAssessmentDialog;
